import { useContext } from "react";
import { IntlProvider } from "react-intl";
import contactMessages from "../translate/contactMessages";
import Logo from "../images/contact.png";
import Me from "../images/DSC_1646.JPG";
import { LangContext } from "../App";
import { Helmet } from "react-helmet-async";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import LanguageIcon from "@mui/icons-material/Language";
import BusinessIcon from "@mui/icons-material/Business";
import { motion, AnimatePresence } from "framer-motion";
import { textVariants } from "../components/textVariants";
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Divider,
  Chip,
  Link as MuiLink,
  Avatar,
} from "@mui/material";

const Contact = () => {
  const language = useContext(LangContext);
  const styles = {
    wrapper: {
      minHeight: "100vh",
      padding: "80px 24px 60px 24px",
      fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
    },
    container: {
      maxWidth: "1200px",
      margin: "0 auto",
      width: "100%",
    },
    headerZone: {
      textAlign: "center",
      marginBottom: "48px",
    },
    sectionSubtitle: {
      fontSize: "14px",
      fontWeight: "700",
      color: "#4f46e5",
      textTransform: "uppercase",
      letterSpacing: "0.1em",
      marginBottom: "8px",
    },
    mainTitle: {
      fontSize: "36px",
      fontWeight: "800",
      color: "#0f172a",
      letterSpacing: "-0.03em",
      margin: 0,
    },
    splitLayout: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(min(450px, 100%), 1fr))",
      gap: "32px",
      alignItems: "start",
    },
    competenceCard: {
      background: "#ffffff",
      borderRadius: "24px",
      padding: "40px",
      border: "1px solid #e2e8f0",
      boxShadow: "0 10px 30px -15px rgba(0,0,0,0.05)",
      boxSizing: "border-box",
    },
    cardHeader: {
      display: "flex",
      alignItems: "center",
      gap: "16px",
      marginBottom: "28px",
      borderBottom: "1px solid #f1f5f9",
      paddingBottom: "20px",
    },
    iconWrapper: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "48px",
      height: "48px",
      borderRadius: "12px",
      backgroundColor: "#eef2ff",
      color: "#4f46e5",
    },
    cardTitle: {
      fontSize: "18px",
      fontWeight: "700",
      color: "#0f172a",
      margin: 0,
    },
    list: {
      display: "flex",
      flexDirection: "column",
      gap: "16px",
    },
    listItem: {
      display: "flex",
      alignItems: "flex-start",
      gap: "12px",
      fontSize: "15px",
      color: "#475569",
      lineHeight: "1.5",
    },
    checkIcon: {
      flexShrink: 0,
      marginTop: "3px",
      color: "#10b981",
    },
  };
  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            name: "Kontakt - Fullstack Developer",
            url: "https://michaljaskulski.pl/contact",
            mainEntity: {
              "@type": "Person",
              name: "Michał Jaskulski",
              jobTitle: "Fullstack Developer",
              telephone: "+48602209858",
              email: "kontakt@michaljaskulski.pl",
              url: "https://michaljaskulski.pl",
            },
          })}
        </script>
        <title>Mikołów | Katowice | Śląskie</title>
        <meta name="description" content="Mikołów, Katowice, Śląskie" />
      </Helmet>

      <Box
        className="bodyBackground"
        sx={{
          backgroundColor: "#f8fafc",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          py: { xs: 4, md: 8 },
          px: { xs: 2, sm: 3 },
        }}
      >
        <IntlProvider locale={language} messages={contactMessages[language]}>
          <AnimatePresence>
            <Container
              component={motion.div}
              maxWidth="lg"
              initial="hidden"
              animate="visible"
              exit="hidden"
              variants={textVariants}
              disableGutters
            >
              <Grid container spacing={4} alignItems="stretch">
                {/* Kolumna lewa: Karta ze zdjęciem i danymi kontaktowymi */}
                <Grid item xs={12} lg={5} sx={{ display: "flex" }}>
                  <Card
                    elevation={0}
                    sx={{
                      background: "#ffffff",
                      borderRadius: "24px",
                      p: { xs: 3, sm: 4 },
                      boxShadow: "0 10px 30px -10px rgba(0,0,0,0.06)",
                      border: "1px solid #f3f4f6",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <Box>
                      {/* Sekcja profilowa (Zdjęcie + Imię i Nazwisko) */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2.5,
                          mb: 4,
                          flexWrap: "wrap",
                        }}
                      >
                        <Box
                          component="img"
                          src={Me}
                          alt="Michał Jaskulski - Fullstack React Developer"
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />

                        <h1 style={styles.mainTitle}>Michał Jaskulski</h1>
                      </Box>

                      {/* Linki kontaktowe */}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 2.5,
                        }}
                      >
                        <ContactLink
                          href="tel:+48602209858"
                          icon={<PhoneAndroidIcon fontSize="small" />}
                          text="+48 602 209 858"
                        />

                        <ContactLink
                          href="mailto:michal.jaskulski@allworks.pl"
                          icon={<AlternateEmailIcon fontSize="small" />}
                          text="michal.jaskulski@allworks.pl"
                        />

                        <ContactLink
                          href="https://michaljaskulski.pl"
                          target="_blank"
                          rel="noreferrer"
                          icon={<LanguageIcon fontSize="small" />}
                          text="michaljaskulski.pl"
                        />
                      </Box>
                    </Box>

                    {/* Stopka karty lewej */}
                    <Box>
                      <Divider sx={{ my: 4, borderColor: "#f1f5f9" }} />
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2.5,
                        }}
                      >
                        {Logo && (
                          <Box
                            component="img"
                            src={Logo}
                            alt="Logo"
                            sx={{
                              height: "45px",
                              objectFit: "contain",
                            }}
                          />
                        )}
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              m: 0,
                              fontSize: "14px",
                              color: "#475569",
                              fontWeight: 500,
                              lineHeight: 1.4,
                            }}
                          >
                            <BusinessIcon
                              fontSize="small"
                              sx={{
                                color: "#94a3b8",
                                mr: 0.5,
                                verticalAlign: "text-bottom",
                              }}
                            />
                            43-190 Mikołów <br /> ul. Brzozowa 14D
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              display: "block",
                              mt: 0.5,
                              fontSize: "12px",
                              color: "#475569",
                              letterSpacing: "0.05em",
                            }}
                          >
                            NIP: 635 184 83 80
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Card>
                </Grid>

                {/* Kolumna prawa: Karta z nagłówkiem i opisem oferty */}
                <Grid item xs={12} lg={7} sx={{ display: "flex" }}>
                  <Card
                    elevation={0}
                    sx={{
                      background:
                        "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
                      borderRadius: "24px",
                      p: { xs: 3, sm: 5 },
                      color: "#f8fafc",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      width: "100%",
                      boxShadow: "0 20px 40px -15px rgba(15,23,42,0.3)",
                    }}
                  >
                    <CardContent sx={{ p: "0 !important" }}>
                      {language === "pl" ? (
                        <>
                          <Typography
                            variant="h4"
                            component="h2"
                            sx={{
                              fontSize: { xs: "22px", sm: "26px" },
                              fontWeight: 800,
                              lineHeight: 1.3,
                              mb: 2.5,
                              color: "#ffffff",
                            }}
                          >
                            Masz pomysł na aplikację lub chcesz rozwinąć
                            istniejący system?
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              fontSize: "16px",
                              lineHeight: 1.7,
                              color: "#cbd5e1",
                              mb: 3,
                            }}
                          >
                            Opisz krótko swoje potrzeby — pomogę dobrać
                            rozwiązanie, zaproponuję kierunek techniczny i
                            przedstawię możliwy zakres prac. Współpracuję z
                            firmami, które traktują technologię jako inwestycję
                            w rozwój.
                          </Typography>
                          <Chip
                            label="Odpowiadam zwykle w ciągu 24 godzin."
                            sx={{
                              fontSize: "13px",
                              fontWeight: 600,
                              color: "#38bdf8",
                              backgroundColor: "rgba(56,189,248,0.1)",
                              borderRadius: "99px",
                              py: 0.5,
                              px: 0.5,
                            }}
                          />
                        </>
                      ) : (
                        <>
                          <Typography
                            variant="h4"
                            component="h2"
                            sx={{
                              fontSize: { xs: "22px", sm: "26px" },
                              fontWeight: 800,
                              lineHeight: 1.3,
                              mb: 2.5,
                              color: "#ffffff",
                            }}
                          >
                            Have an idea for a web application or looking to
                            improve an existing system?
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              fontSize: "16px",
                              lineHeight: 1.7,
                              color: "#cbd5e1",
                              mb: 3,
                            }}
                          >
                            Tell me about your project and requirements. I'll
                            help you identify the best solution, recommend the
                            right technical approach, and define the scope of
                            work. I partner with businesses that view technology
                            as a strategic investment in growth.
                          </Typography>
                          <Chip
                            label="I typically respond within one business day."
                            sx={{
                              fontSize: "13px",
                              fontWeight: 600,
                              color: "#38bdf8",
                              backgroundColor: "rgba(56,189,248,0.1)",
                              borderRadius: "99px",
                              py: 0.5,
                              px: 0.5,
                            }}
                          />
                        </>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Container>
          </AnimatePresence>
        </IntlProvider>
      </Box>
    </>
  );
};

// Pomocniczy komponent do spójnego renderowania linków kontaktowych
const ContactLink = ({ href, icon, text, target, rel }) => (
  <MuiLink
    href={href}
    target={target}
    rel={rel}
    underline="none"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 1.75,
      color: "#334155",
      fontSize: "16px",
      fontWeight: 500,
      transition: "color 0.2s ease",
      "&:hover": {
        color: "#4f46e5",
      },
    }}
  >
    <Box
      component="span"
      sx={{
        color: "#4f46e5",
        backgroundColor: "#eef2ff",
        p: 1,
        borderRadius: "10px",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {icon}
    </Box>
    {text}
  </MuiLink>
);

export default Contact;
